import type { CatalogoResponse, FaixaPrecoProduto, ProdutoCatalogo } from '../api';
import type { ItemCarrinho } from '../store/carrinho-slice';
import { precoPorQuantidade } from './preco-por-quantidade';

export interface AlteracaoPreco {
  id: string;
  nome: string;
  de: number;
  para: number;
}

export interface ResultadoReconciliacao {
  itens: ItemCarrinho[];
  alteracoes: AlteracaoPreco[];
  removidos: ItemCarrinho[];
}

function indexarProdutos(catalogo: CatalogoResponse): Map<number, ProdutoCatalogo> {
  const porId = new Map<number, ProdutoCatalogo>();
  for (const grupo of catalogo.grupos) {
    for (const produto of grupo.produtos) porId.set(produto.id, produto);
  }
  return porId;
}

const TOLERANCIA = 0.01;

/**
 * Contribuição total dos passos ao preço unitário do item, ou `null` quando
 * algum passo não trouxe `valorPasso` (carrinho antigo, de antes do campo).
 * Sem ele não dá para separar o preço do pai dos extras — e reconciliar às
 * cegas cobraria o produto sem os adicionais.
 */
function extrasDosPassos(passos: { valorPasso?: number }[]): number | null {
  let extras = 0;
  for (const passo of passos) {
    if (typeof passo.valorPasso !== 'number') return null;
    extras += passo.valorPasso;
  }
  return extras;
}

function faixasIguais(
  a: FaixaPrecoProduto[] | undefined,
  b: FaixaPrecoProduto[] | undefined,
): boolean {
  const umas = a ?? [];
  const outras = b ?? [];
  if (umas.length !== outras.length) return false;
  return umas.every((faixa, i) => {
    const outra = outras[i]!;
    return (
      faixa.quantidadeMinima === outra.quantidadeMinima
      && faixa.quantidadeMaxima === outra.quantidadeMaxima
      && faixa.valorUnitario === outra.valorUnitario
    );
  });
}

export function reconciliarCarrinho(
  itens: ItemCarrinho[],
  catalogo: CatalogoResponse | undefined,
): ResultadoReconciliacao {
  if (!catalogo || itens.length === 0) return { itens, alteracoes: [], removidos: [] };

  const produtos = indexarProdutos(catalogo);
  const alteracoes: AlteracaoPreco[] = [];
  const removidos: ItemCarrinho[] = [];
  let mudouAlgum = false;

  const conciliados: ItemCarrinho[] = [];

  for (const item of itens) {
    const produto = produtos.get(item.idProduto);

    if (!produto) {
      removidos.push(item);
      mudouAlgum = true;
      continue;
    }

    // Item com passos: reconcilia só o preço do PAI, isolando-o pelos `valorPasso`.
    // Mudança de preço das OPÇÕES de passo continua fora — exigiria refazer o cálculo
    // da tabela de preço respeitando UNITARIO/FRACAO/MAIOR_VALOR.
    if (item.passos && item.passos.length > 0) {
      const extras = extrasDosPassos(item.passos);

      if (extras === null) {
        conciliados.push(item);
        continue;
      }

      const paiAtual = item.precoUnitario - extras;
      if (Math.abs(produto.valor - paiAtual) <= TOLERANCIA) {
        conciliados.push(item);
        continue;
      }

      const novoPreco = Number((produto.valor + extras).toFixed(2));
      alteracoes.push({ id: item.id, nome: item.nome, de: item.precoUnitario, para: novoPreco });
      mudouAlgum = true;
      // `precoUnitarioBase` fica intocado de propósito: para item com passos ele é a
      // base de recálculo por quantidade, e gravar `produto.valor` puro perderia os extras.
      conciliados.push({ ...item, precoUnitario: novoPreco });
      continue;
    }

    const novoPreco = precoPorQuantidade(produto.faixasPreco, item.quantidade, produto.valor);
    const precoMudou = Math.abs(novoPreco - item.precoUnitario) > TOLERANCIA;
    const faixasMudaram = !faixasIguais(item.faixasPreco, produto.faixasPreco);

    if (!precoMudou && !faixasMudaram) {
      conciliados.push(item);
      continue;
    }

    if (precoMudou) {
      alteracoes.push({ id: item.id, nome: item.nome, de: item.precoUnitario, para: novoPreco });
    }

    mudouAlgum = true;
    conciliados.push({
      ...item,
      precoUnitario: novoPreco,
      precoUnitarioBase: produto.valor,
      faixasPreco: produto.faixasPreco,
    });
  }

  return { itens: mudouAlgum ? conciliados : itens, alteracoes, removidos };
}

/**
 * O carrinho ainda bate com o cardápio? Quem finaliza sem abrir o carrinho pergunta
 * antes: a conferência de preço e o aviso ao cliente moram no carrinho.
 */
export function carrinhoEmDia(itens: ItemCarrinho[], catalogo: CatalogoResponse | undefined): boolean {
  const { alteracoes, removidos } = reconciliarCarrinho(itens, catalogo);
  return alteracoes.length === 0 && removidos.length === 0;
}

export function avisoDeMudancaDePreco(
  alteracoes: AlteracaoPreco[],
  formatar: (valor: number) => string,
): { tom: 'success' | 'warning'; texto: string } | null {
  if (alteracoes.length === 0) return null;

  const subiu = alteracoes.filter((a) => a.para > a.de);
  const tom = subiu.length > 0 ? 'warning' : 'success';

  if (alteracoes.length === 1) {
    const alteracao = alteracoes[0]!;
    const verbo = alteracao.para > alteracao.de ? 'subiu' : 'caiu';
    return { tom, texto: `O preço de ${alteracao.nome} ${verbo} para ${formatar(alteracao.para)}.` };
  }

  if (subiu.length === 0) {
    return { tom, texto: `O preço de ${alteracoes.length} itens caiu. O total já está atualizado.` };
  }

  return { tom, texto: `O preço de ${alteracoes.length} itens mudou. Confira antes de continuar.` };
}

export interface LinhaTotem {
  id: string;
  idProduto: number;
  nome: string;
  quantidade: number;
  valorUnitario: number;
  valorUnitarioBase?: number;
  temPassos: boolean;
  passos?: { valorPasso?: number }[];
  faixasPreco?: FaixaPrecoProduto[];
}

export interface CorrecaoLinhaTotem {
  id: string;
  valorUnitario: number;
  valorUnitarioBase: number;
  valorTotal: number;
  faixasPreco?: FaixaPrecoProduto[];
}

export interface ReconciliacaoTotem {
  correcoes: CorrecaoLinhaTotem[];
  alteracoes: AlteracaoPreco[];
}

export function reconciliarCarrinhoTotem(
  itens: LinhaTotem[],
  catalogo: CatalogoResponse | undefined,
): ReconciliacaoTotem {
  const vazio: ReconciliacaoTotem = { correcoes: [], alteracoes: [] };
  if (!catalogo || itens.length === 0) return vazio;

  const produtos = indexarProdutos(catalogo);
  const correcoes: ReconciliacaoTotem['correcoes'] = [];
  const alteracoes: AlteracaoPreco[] = [];

  for (const item of itens) {
    const produto = produtos.get(item.idProduto);
    if (!produto) continue;

    // Mesmo recorte do carrinho: reconcilia o pai pelos `valorPasso`, não as opções de passo.
    if (item.temPassos) {
      const extras = item.passos ? extrasDosPassos(item.passos) : null;
      if (extras === null) continue;

      const paiAtual = item.valorUnitario - extras;
      if (Math.abs(produto.valor - paiAtual) <= TOLERANCIA) continue;

      const novoUnitario = Number((produto.valor + extras).toFixed(2));
      alteracoes.push({ id: item.id, nome: item.nome, de: item.valorUnitario, para: novoUnitario });
      correcoes.push({
        id: item.id,
        valorUnitario: novoUnitario,
        // com passos a base de recálculo é o unitário cheio: `produto.valor` puro perderia os extras
        valorUnitarioBase: novoUnitario,
        valorTotal: Number((novoUnitario * item.quantidade).toFixed(2)),
        faixasPreco: item.faixasPreco,
      });
      continue;
    }

    const novoPreco = precoPorQuantidade(produto.faixasPreco, item.quantidade, produto.valor);
    const precoMudou = Math.abs(novoPreco - item.valorUnitario) > TOLERANCIA;
    const faixasMudaram = !faixasIguais(item.faixasPreco, produto.faixasPreco);

    if (!precoMudou && !faixasMudaram) continue;

    if (precoMudou) {
      alteracoes.push({ id: item.id, nome: item.nome, de: item.valorUnitario, para: novoPreco });
    }

    correcoes.push({
      id: item.id,
      valorUnitario: novoPreco,
      valorUnitarioBase: produto.valor,
      valorTotal: Number((novoPreco * item.quantidade).toFixed(2)),
      faixasPreco: produto.faixasPreco,
    });
  }

  return { correcoes, alteracoes };
}
